import React, { useMemo } from 'react';
import NoMachinesEmptyState from 'app/components/access-sharing/EquipmentAndFleets/NoMachinesEmptyState';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import {
  AccessSharingStepController,
  EquipmentSelectionStepController,
  EquipmentsSharingOverviewStepController,
} from './components';
import { useNavigate } from 'react-router-dom';
import {
  AccessSharingFlowProvider,
  useAccessSharingFlowContext,
} from 'app/hooks/access-sharing';
import { ACCESS_SHARING_EQUIPMENT_STEPS } from './constants';
import useNavigationPrompt from 'app/hooks/useNavigationPrompt';
import { ConfirmNavigateAwayDialog } from 'app/components/dialogs';
import { useIsMobile } from 'app/hooks/useIsMobile';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';

export default function ShareEquipmentPage() {
  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  const isMobile = useIsMobile();

  // TODO throw it away as soon as possible
  // Refactor layout components in the future to be more flexible
  // and avoid hacks like this.
  React.useEffect(() => {
    let classNameIndex = -1;

    if (!isMobile) {
      classNameIndex = setClassNames('layout-grey');
    }
    return () => {
      if (classNameIndex >= 0) {
        removeClassNames(classNameIndex);
        classNameIndex = -1;
      }
    };
  }, [isMobile]);

  return (
    <AccessSharingFlowProvider
      stepCount={Object.keys(ACCESS_SHARING_EQUIPMENT_STEPS).length}
      // startStep={ACCESS_SHARING_EQUIPMENT_STEPS.OVERVIEW}
    >
      <StepRenderer />
      <UnsavedChangesWarning />
    </AccessSharingFlowProvider>
  );
}

function StepRenderer() {
  const navigate = useNavigate();
  const { stepper } = useAccessSharingFlowContext();
  const { step } = stepper;

  const { equipments: rawEquipments } = useGetEquipmentData();

  const equipments = useMemo(() => {
    return rawEquipments?.filter(equipment => {
      if (equipment.type !== 'machine' && equipment.type !== 'attachment')
        return false;
      return (
        equipment.role === AccessSharingRole.Admin ||
        equipment.role === AccessSharingRole.Owner
      );
    });
  }, [rawEquipments]);

  function handleBack() {
    navigate('/access/equipment-and-fleets');
  }

  if (!equipments?.length) {
    return <NoMachinesEmptyState onClose={() => handleBack()} />;
  }

  switch (step) {
    case ACCESS_SHARING_EQUIPMENT_STEPS.EQUIPMENT_SELECTION:
      return (
        <EquipmentSelectionStepController
          equipment={equipments}
          onBack={handleBack}
        />
      );
    case ACCESS_SHARING_EQUIPMENT_STEPS.ACCESS_SHARING:
      return <AccessSharingStepController />;
    case ACCESS_SHARING_EQUIPMENT_STEPS.OVERVIEW:
      return (
        <EquipmentsSharingOverviewStepController
          equipment={equipments}
          onBack={handleBack}
        />
      );
    default:
      return null;
  }
}

function UnsavedChangesWarning() {
  const {
    isSubmitted,
    isEditing,
    selectedUsers,
    selectedEquipmentIds,
    stepper,
  } = useAccessSharingFlowContext();
  const { step } = stepper;

  const isDirty = selectedUsers.size > 0 || selectedEquipmentIds.size > 0;
  const isStepWithPrompt = step === ACCESS_SHARING_EQUIPMENT_STEPS.OVERVIEW;

  const { showPrompt, confirmNavigation, cancelNavigation } =
    useNavigationPrompt(
      isDirty && (isStepWithPrompt || isEditing) && !isSubmitted,
    );

  return (
    <ConfirmNavigateAwayDialog
      open={showPrompt}
      onConfirm={confirmNavigation}
      onClose={cancelNavigation}
    />
  );
}
